import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, signInWithCustomToken, Auth, onAuthStateChanged, User, signOut } from 'firebase/auth';
import {
  Firestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { getMessaging, getToken, onMessage, Messaging, MessagePayload } from 'firebase/messaging';
import { getStorage, FirebaseStorage } from 'firebase/storage';

/** Where Firebase's own auth handler is served from when we can't self-host. */
const FALLBACK_AUTH_DOMAIN = "auth.outclose.com";

/**
 * Whether Firebase's auth handler is first-party to this page.
 *
 * The precondition for redirect sign-in. When it isn't met the redirect still
 * *leaves* perfectly well — it is the return leg that comes back empty, because
 * the handler was denied the storage holding the credential. That failure is
 * indistinguishable from a browser that refuses redirects outright, so it trips
 * the session-wide fallback and every later attempt silently becomes a popup.
 *
 * Checking up front turns that into a deterministic, explainable choice: the
 * popup on the http dev server, where the handler is `auth.outclose.com`, and
 * the redirect on any deploy, where the app proxies `/__/auth/*` itself. It
 * follows that the redirect arm is only exercisable against a deploy — a
 * preview one is enough, and matches production exactly.
 */
export function isAuthHandlerSameOrigin(): boolean {
  if (typeof window === "undefined" || !window.location?.host) return false;
  return resolveAuthDomain() === window.location.host;
}

/**
 * The origin Firebase loads `/__/auth/*` from.
 *
 * Same-origin with the app wherever possible, which is what redirect sign-in
 * requires: Safari and Firefox refuse the auth handler the storage it needs
 * when it's a third party, so `getRedirectResult` comes back empty on exactly
 * the browsers redirect exists to serve. Everything that serves this app over
 * https proxies the handler through to Firebase, via
 * `client/functions/__/auth/[[path]].js`.
 *
 * Popups ride on the same setting and are no worse for it (a same-origin popup
 * dodges the third-party rules too).
 *
 * **The https check is load-bearing, not a nicety.** Firebase builds the handler
 * URL as `https://${authDomain}/__/auth/handler` with the scheme hardcoded —
 * outside the emulator there is no http form. Point `authDomain` at a host
 * that only answers on http and every sign-in, popup and redirect alike, walks
 * into an SSL error: the plain `npm run dev` server on localhost is exactly
 * that host. So the same-origin swap is conditional on the page itself being
 * https, and http dev falls back to the standalone domain, which is what the
 * popup used before any of this.
 *
 * Off the browser — tests, any SSR — there is no origin to be same as, so the
 * standalone auth domain stands in there too.
 */
export function resolveAuthDomain(): string {
  if (typeof window === "undefined" || !window.location?.host) {
    return FALLBACK_AUTH_DOMAIN;
  }
  if (window.location.protocol !== "https:") {
    return FALLBACK_AUTH_DOMAIN;
  }
  return window.location.host;
}

// Firebase configuration - loaded from environment variables
const firebaseConfig = {
  apiKey: "AIzaSyAFz2b_722kKbQxadNBacV6n2R19sB00Io",
  authDomain: resolveAuthDomain(),
  projectId: "gayborhood-471323",
  storageBucket: "gayborhood-471323.firebasestorage.app",
  messagingSenderId: "442699997029",
  appId: "1:442699997029:web:1d9939034ac045d684cb8c"
};

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;
let messaging: Messaging | null = null;
let storage: FirebaseStorage | null = null;

/**
 * Check if Firebase is configured
 */
export function isFirebaseConfigured(): boolean {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.authDomain &&
    firebaseConfig.projectId
  );
}

/**
 * Initialize Firebase (lazy)
 */
export function initFirebase(): FirebaseApp {
  if (!app) {
    if (!isFirebaseConfigured()) {
      throw new Error('Firebase is not configured. Please set VITE_FIREBASE_* environment variables.');
    }
    app = initializeApp(firebaseConfig);
  }
  return app;
}

/**
 * Get Firebase Auth instance
 */
export function getFirebaseAuth(): Auth {
  if (!auth) {
    auth = getAuth(initFirebase());
  }
  return auth;
}

/**
 * Get Firebase Storage instance. Uploads carry the currently-signed-in
 * user's auth token automatically, so Storage Rules can gate on
 * `request.auth.uid`.
 */
export function getFirebaseStorage(): FirebaseStorage {
  if (!storage) {
    storage = getStorage(initFirebase());
  }
  return storage;
}

// Named database ID for chat
const FIRESTORE_DATABASE_ID = "chat";

/**
 * Get Firestore instance for the chat database
 * Configured with persistent local cache for offline support
 */
export function getFirestoreDb(): Firestore {
  if (!db) {
    db = initializeFirestore(
      initFirebase(),
      {
        localCache: persistentLocalCache({
          tabManager: persistentMultipleTabManager(),
        }),
      },
      FIRESTORE_DATABASE_ID
    );
  }
  return db;
}

/**
 * Sign in to Firebase with a custom token from our backend
 */
export async function signInToFirebaseWithCustomToken(customToken: string): Promise<User> {
  const auth = getFirebaseAuth();
  const userCredential = await signInWithCustomToken(auth, customToken);
  return userCredential.user;
}

export async function signOutFromFirebase(): Promise<void> {
  const auth = getFirebaseAuth();
  await signOut(auth);
}

/**
 * Get current Firebase user
 */
export function getCurrentFirebaseUser(): User | null {
  const auth = getFirebaseAuth();
  return auth.currentUser;
}

/**
 * Listen to Firebase auth state changes
 */
export function onFirebaseAuthStateChanged(callback: (user: User | null) => void): () => void {
  const auth = getFirebaseAuth();
  return onAuthStateChanged(auth, callback);
}

// ============================================================================
// FIREBASE CLOUD MESSAGING (Push Notifications)
// ============================================================================

// VAPID key for web push - this is the public key from Firebase Console
const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

/**
 * Get Firebase Messaging instance (lazy)
 */
export function getFirebaseMessaging(): Messaging | null {
  if (!messaging) {
    try {
      messaging = getMessaging(initFirebase());
    } catch (error) {
      console.warn('Firebase Messaging not supported:', error);
      return null;
    }
  }
  return messaging;
}

/**
 * Check if push notifications are supported
 */
export function isPushNotificationsSupported(): boolean {
  return 'Notification' in window && 'serviceWorker' in navigator && !!VAPID_KEY;
}

/**
 * Request notification permission and get FCM token
 * @returns The FCM token if granted, null if denied or unsupported
 */
export async function requestNotificationPermission(): Promise<string | null> {
  if (!isPushNotificationsSupported()) {
    console.warn('Push notifications not supported');
    return null;
  }

  try {
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return null;
    }

    const fcmMessaging = getFirebaseMessaging();
    if (!fcmMessaging) {
      return null;
    }

    // Get the FCM token
    const token = await getToken(fcmMessaging, { vapidKey: VAPID_KEY });
    
    if (token) {
      console.log('FCM token obtained');
      return token;
    } else {
      console.warn('No FCM token available');
      return null;
    }
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
}

/**
 * Get the current FCM token without requesting permission
 * @returns The FCM token if available, null otherwise
 */
export async function getCurrentFcmToken(): Promise<string | null> {
  if (!isPushNotificationsSupported()) {
    return null;
  }

  if (Notification.permission !== 'granted') {
    return null;
  }

  try {
    const fcmMessaging = getFirebaseMessaging();
    if (!fcmMessaging) {
      return null;
    }

    const token = await getToken(fcmMessaging, { vapidKey: VAPID_KEY });
    return token || null;
  } catch (error) {
    console.error('Error getting current FCM token:', error);
    return null;
  }
}

/**
 * Listen for foreground messages
 * @returns Unsubscribe function
 */
export function onForegroundMessage(callback: (payload: MessagePayload) => void): () => void {
  const fcmMessaging = getFirebaseMessaging();
  if (!fcmMessaging) {
    return () => {};
  }

  return onMessage(fcmMessaging, callback);
}

